/* eslint-disable max-lines */
/* istanbul ignore file */
import { LBButton, LBXIcon } from '@linearb/buzz';
import { Link, Tooltip } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import { DeleteOutlined } from '@material-ui/icons';
import QueryBuilderIcon from '@material-ui/icons/QueryBuilder';
import {
  BACKFILL_STATE,
  DATE_AND_TIME_FORMAT,
  DATE_FORMAT,
  GIT_PROVIDERS,
  REPOSITORIES_STATE
} from 'common/constants/constants';
import { SPACES } from 'common/constants/sizes';
import { navigateBookDemoClick } from 'common/utils/utils';
import Checkbox from 'components/base/Checkbox';
import Icon from 'components/base/Icon';
import Text from 'components/base/Text';
import {
  DarkBlueWrapper,
  Flex,
  Padding,
  TagWrapper
} from 'components/base/layout-components';
import capitalize from 'lodash/capitalize';
import get from 'lodash/get';
import moment from 'moment';
import styled from 'styled-components';
import RepoGitStreamEnabledCheckbox from 'views/root/views/home/views/settings/views/RepositoriesV2/gitStreamIntegration/RepoGitStreamEnabledCheckbox';
import {
  getDisableGitStreamConfirmationModalOptions,
  isCmRepo,
  onDisconnectGitStream,
  updateActivationGitStreamForRepo
} from 'views/root/views/home/views/settings/views/RepositoriesV2/gitStreamIntegration/gitStream.service';

const BACKFILL_IN_PROGRESS_STATES = ['initiated', 'started', 'in-progress'];

export const createModalWrapper =
  (title, toggleModal) =>
  ({ children }) => {
    return (
      <Flex width='100%' height='100%' content='center' direction='column'>
        <Flex
          direction='row'
          justify='space-between'
          items='center'
          padding='x-small'
        >
          <Text size='large' pt={SPACES['x-small']} pl={SPACES.small}>
            Integration name: {title}
          </Text>
          <LBButton
            onClick={() => toggleModal()}
            id='closeModal'
            size='icon'
            variant='ghost'
          >
            <LBXIcon />
          </LBButton>
        </Flex>
        {children}
      </Flex>
    );
  };

const FailingRepositoryTooltipTitle = (
  <Padding padding='tiny'>
    <Flex direction='column'>
      <Text size='large' color='chambray' pb='0.5rem'>
        This repository couldn't be scanned by LinearB
      </Text>
      <Text size='medium'>
        This usually happens when a repository is either renamed or deleted.
      </Text>
    </Flex>
  </Padding>
);

export const getStatusIconAndText = repository => {
  const { state, updated_at, failing_reason, created_at } = repository;

  const backfillState =
    repository?.extra?.backfill_status ?? BACKFILL_STATE.COMPLETED;

  const isNewRepo = moment().diff(moment(created_at), 'minutes') < 2;

  const forceInitiated =
    isNewRepo && backfillState === BACKFILL_STATE.COMPLETED;

  if (state === REPOSITORIES_STATE.INACTIVE) {
    return (
      <Flex items='center'>
        <Icon name='WarningOutline' size='small' color='osloGray' />
        <Text size='large' pl='0.7rem' color='osloGray'>
          {`Repository not tracked since ${moment(updated_at).format(
            DATE_FORMAT
          )}`}
        </Text>
      </Flex>
    );
  }
  if (
    (state === REPOSITORIES_STATE.FAILING ||
      backfillState === BACKFILL_STATE.FAILING) &&
    failing_reason
  ) {
    return (
      <Flex items='center'>
        <Icon name='WarningOutline' size='small' color='torchRed' />
        <Tooltip title={FailingRepositoryTooltipTitle}>
          <Text size='large' pl='0.7rem' color='torchRed'>
            {capitalize(failing_reason)}
          </Text>
        </Tooltip>
      </Flex>
    );
  }
  if (BACKFILL_IN_PROGRESS_STATES.includes(backfillState) || forceInitiated) {
    if (
      isBackfillRunInLast24Hours(repository) &&
      repository.state === REPOSITORIES_STATE.ACTIVE
    ) {
      // Repository backfill in-progress will show up if:
      // - A backfill run has occurred in the last 24 hours.
      // - Repository is active.
      // - Backfill process is in progress.
      return (
        <Flex items='center'>
          <QueryBuilderIcon fontSize='medium' />
          <Text
            size='large'
            weight={state === REPOSITORIES_STATE.FAILING ? '100' : '400'}
            pl='0.7rem'
          >
            {backfillState === BACKFILL_STATE.INITIATED || forceInitiated
              ? 'Initiated backfill'
              : 'Backfill in-progress'}
          </Text>
        </Flex>
      );
    }
  }
  if (repository.state === REPOSITORIES_STATE.ACTIVE) {
    // Showing 'Up to date' status even when backfill is not completed until a fix of backfill status is done
    return (
      <Flex items='center'>
        <Icon name='SuccessOutline' size='small' color='nepal' />
        <Text
          size='large'
          weight={state === REPOSITORIES_STATE.FAILING ? '100' : '400'}
          pl='0.7rem'
          color='shuttleGray'
        >
          Up to date
        </Text>
      </Flex>
    );
  }
  if (
    state === REPOSITORIES_STATE.failing &&
    backfillState === BACKFILL_STATE.FAILED
  ) {
    return (
      <Flex items='center'>
        <Icon name='WarningOutline' size='small' color='torchRed' />
        <Text size='large' pl='0.7rem' color='torchRed'>
          Backfill failed
        </Text>
      </Flex>
    );
  }
  return (
    <Flex items='center'>
      <Icon name='WarningOutline' size='small' color='torchRed' />
      <Tooltip title={FailingRepositoryTooltipTitle}>
        <Text size='large' pl='0.7rem' color='torchRed'>
          Repo not found
        </Text>
      </Tooltip>
    </Flex>
  );
};

export const getRepositoryTrackedSince = repository => (
  <Text
    size='large'
    weight={repository.state === REPOSITORIES_STATE.FAILING ? '100' : '400'}
  >
    {getRepositoryTrackedSinceDate(repository)}
  </Text>
);

const getRepositoryTrackedSinceDate = repository => {
  const scan_start_date = get(repository, 'extra.tracked_since', null);
  return scan_start_date ? moment(scan_start_date).format(DATE_FORMAT) : '-';
};

const getRepositoryCheckbox = ({
  repository,
  updateRepository,
  debouncedToggleSnackBar,
  toggleSnackBar,
  getSensorAuthorizations,
  onCheck,
  isChecked
}) => {
  const isCheckControlled = onCheck !== undefined && isChecked !== undefined;

  const onSelectClick = async repository => {
    const state =
      repository.state === REPOSITORIES_STATE.ACTIVE
        ? REPOSITORIES_STATE.INACTIVE
        : REPOSITORIES_STATE.ACTIVE;

    if (isCheckControlled) return onCheck(repository, !isChecked);

    try {
      // For gitlab we should check GitStream integration status
      await updateActivationGitStreamForRepo(
        repository,
        state,
        updateRepository,
        getSensorAuthorizations
      );
      await updateRepository(
        repository.id,
        {
          state
        },
        repository.backfill_metadata
      );
      debouncedToggleSnackBar(
        'Your repository list has been updated successfully.',
        'NOTE: It might take a couple of hours to see updated data reflecting this change.',
        true,
        toggleSnackBar
      );
    } catch (e) {
      const exceededMessage = get(e, 'response.data.description[0].msg', '');
      const errorMessage = exceededMessage
        .toLowerCase()
        .includes('repositories limit')
        ? exceededMessage
        : 'There was an error updating this repository.';
      debouncedToggleSnackBar(errorMessage, '', false, toggleSnackBar);
    }
  };
  const backfillState =
    repository?.extra?.backfill_status ?? BACKFILL_STATE.COMPLETED;

  const isEnabledCheckBox =
    isCheckControlled ||
    (repository.state !== REPOSITORIES_STATE.FAILING &&
      (!BACKFILL_IN_PROGRESS_STATES.includes(backfillState) ||
        !isBackfillRunInLast24Hours(repository))); // If backfill is in progress more than 24hours enable the checkbox
  return isEnabledCheckBox ? (
    <Checkbox
      checked={
        isCheckControlled
          ? isChecked
          : repository.state === REPOSITORIES_STATE.ACTIVE
      }
      onChange={() => onSelectClick(repository)}
      color='primary'
      inputProps={{ 'aria-label': 'Select' }}
    />
  ) : (
    <Tooltip title={FailingRepositoryTooltipTitle}>
      <Checkbox
        checked
        disabled
        color='primary'
        inputProps={{ 'aria-label': 'Select' }}
      />
    </Tooltip>
  );
};

export const resolveRepoName = ({ repositoryName, sensorId, url }) => {
  return sensorId === GIT_PROVIDERS.GITLAB || sensorId === GIT_PROVIDERS.GITHUB
    ? url
      ? url.pathname.substring(1).split(/.git$/)[0]
      : repositoryName
    : repositoryName;
};

export const getRepositoryName = ({
  repository,
  updateRepository,
  debouncedToggleSnackBar,
  toggleSnackBar,
  getSensorAuthorizations,
  selectionProps
}) => {
  const {
    sensor_id: sensorId,
    http_url: repoUrl,
    repository_group_name: repositoryGroupName,
    name: repositoryName = ''
  } = repository || {};
  const url = repoUrl ? new URL(repoUrl) : '';
  const showGroupName =
    sensorId === GIT_PROVIDERS.AZURE_DEVOPS && repositoryGroupName;
  const resolvedRepoName = resolveRepoName({ repositoryName, sensorId, url });

  return (
    <Flex items='center'>
      {getRepositoryCheckbox({
        repository,
        updateRepository,
        debouncedToggleSnackBar,
        toggleSnackBar,
        getSensorAuthorizations,
        ...(selectionProps && {
          onCheck: selectionProps?.toggleSelection,
          isChecked: selectionProps?.selectedIds?.includes(repository?.id)
        })
      })}
      <Text
        pr='1.5rem'
        size='large'
        weight={repository.state === REPOSITORIES_STATE.FAILING ? '100' : '400'}
        data-testid='repositoryName'
      >
        {resolvedRepoName}
      </Text>
      {showGroupName && (
        <Flex items='center' justify='center' pl='0.5rem'>
          <Tooltip title={repositoryGroupName}>
            <TagWrapper
              backgroundColor='zircon'
              borderColor='botticelli'
              data-testid='repositoryGroupName-Tag'
            >
              <Text ellipsisAfter='10rem' weight='400' color='shuttleGray'>
                {repositoryGroupName}
              </Text>
            </TagWrapper>
          </Tooltip>
        </Flex>
      )}
    </Flex>
  );
};

const getRepositorySettings = ({
  repository,
  setSaving,
  handleRepoDeleteClick,
  handleRepoSettingsClick,
  saving
}) => {
  const backfillState =
    repository?.extra?.backfill_status ?? BACKFILL_STATE.COMPLETED;

  if (BACKFILL_IN_PROGRESS_STATES.includes(backfillState)) {
    if (
      isBackfillRunInLast24Hours(repository) &&
      repository.state === REPOSITORIES_STATE.ACTIVE
    ) {
      // Repository backfill spinner will show up if:
      // - A backfill run has occurred in the last 24 hours.
      // - Repository is active.
      // - Backfill process is in progress.
      return (
        <Flex pl='small'>
          <CircularProgress size='1.8rem' color='primary' />
        </Flex>
      );
    }
  }
  if (repository.state !== REPOSITORIES_STATE.FAILING) {
    return (
      <LBButton
        variant='ghost'
        size='icon'
        aria-label='settings'
        onClick={() => handleRepoSettingsClick(repository.name, repository.id)}
      >
        <Icon name='Settings' />
      </LBButton>
    );
  }
  return (
    <LBButton
      variant='ghost'
      size='icon'
      aria-label='delete'
      disabled={saving}
      onClick={async () => {
        setSaving(true);
        await handleRepoDeleteClick(repository);
        setSaving(false);
      }}
    >
      <DeleteOutlined fontSize='large' />
    </LBButton>
  );
};
const LastProcessedAtTooltip = ({ lastProcessedAt }) => (
  <Padding padding='tiny'>
    <Flex direction='column'>
      <Text size='large' color='chambray' pb='0.5rem'>
        This repository was last processed at
      </Text>
      <Text size='medium'>{lastProcessedAt}</Text>
    </Flex>
  </Padding>
);

const getRepositoryProviderLastActivity = repository => {
  const providerLastActivity = get(repository, 'extra.provider_last_activity');
  const lastProcessedAt = get(repository, 'extra.last_processed_at');
  const providerLastActivityFormatted = providerLastActivity
    ? moment(providerLastActivity).format(DATE_AND_TIME_FORMAT)
    : '-';
  const lastProcessedAtFormatted = lastProcessedAt
    ? moment(lastProcessedAt).format(DATE_AND_TIME_FORMAT)
    : '-';
  return (
    <Tooltip
      title={
        <LastProcessedAtTooltip lastProcessedAt={lastProcessedAtFormatted} />
      }
    >
      <Text
        size='large'
        weight={repository.state === REPOSITORIES_STATE.FAILING ? '100' : '400'}
      >
        {providerLastActivityFormatted}
      </Text>
    </Tooltip>
  );
};
export const onRepositoryGitStreamIntegrationClick = async ({
  repository,
  updateRepository,
  getSensorAuthorizations,
  getRepositories,
  isChecked,
  setIsChecked,
  toggleModal
}) => {
  const { sensor_authorization_id: sensorAuthorizationId, name } = repository;
  // if cm repo is being disabled for gitStream - we remove the integration
  if (isCmRepo(name)) {
    if (isChecked) {
      await toggleModal(
        getDisableGitStreamConfirmationModalOptions({
          toggleModal,
          onDisconnect: async () => {
            await onDisconnectGitStream({
              sensorAuthorizationId,
              toggleModal,
              getSensorAuthorizations,
              getRepositories
            });
            setIsChecked(false);
          },
          onCancel: () => {
            toggleModal();
            setIsChecked(true);
          }
        })
      );
    } else {
      await updateRepository(repository.id, {
        extra: { is_gitstream_enabled: true }
      });
      await getSensorAuthorizations({ withGitStreamEnabled: true });
      await getRepositories();
      setIsChecked(false);
    }
  } else {
    await updateRepository(repository.id, {
      extra: { is_gitstream_enabled: !isChecked }
    });
    await getRepositories();
    setIsChecked(!isChecked);
  }
};

export const formatRepository = ({
  repository,
  updateRepository,
  debouncedToggleSnackBar,
  handleRepoSettingsClick,
  saving,
  setSaving,
  handleRepoDeleteClick,
  toggleSnackBar,
  getSensorAuthorizations,
  getRepositories,
  sensorAuthorizations,
  selectionProps
}) => {
  const { sensor_authorization_id: sensorAuthId } = repository;

  const {
    gitstream_enabled: isSensorConnectedToGitStream,
    opa_gitstream_installed: isOrgConnectedToGitstream,
    profile,
    child_sensor_auth_id
  } = sensorAuthorizations?.find(
    sensorAuth => sensorAuth.id === sensorAuthId
  ) ?? {};

  const isOnPrem = profile?.['on-prem'] || false;

  const isGitlabConnectedToGitstream =
    (isOnPrem ? isOrgConnectedToGitstream : isSensorConnectedToGitStream) ||
    false;

  const isGithubServerConnectedToGitstream = !!child_sensor_auth_id;

  const isGitstreamEnabled =
    (repository.sensor_id === GIT_PROVIDERS.GITLAB &&
      isGitlabConnectedToGitstream) ||
    (repository.sensor_id === GIT_PROVIDERS.GITHUB &&
      isGithubServerConnectedToGitstream) ||
    (repository.sensor_id === GIT_PROVIDERS.BITBUCKET &&
      isSensorConnectedToGitStream);

  return {
    name: getRepositoryName({
      repository,
      updateRepository,
      debouncedToggleSnackBar,
      toggleSnackBar,
      getSensorAuthorizations,
      selectionProps
    }),
    provider_last_activity: getRepositoryProviderLastActivity(repository),
    tracked_since: getRepositoryTrackedSince(repository),
    state: getStatusIconAndText(repository),
    is_gitstream_enabled: isGitstreamEnabled ? (
      <RepoGitStreamEnabledCheckbox
        repository={repository}
        updateRepository={updateRepository}
        getSensorAuthorizations={getSensorAuthorizations}
        getRepositories={getRepositories}
        sensorAuthorizations={sensorAuthorizations}
      />
    ) : null,
    settings: getRepositorySettings({
      repository,
      setSaving,
      handleRepoDeleteClick,
      handleRepoSettingsClick,
      saving
    })
  };
};

export const teamTooltip = (
  <>
    Want to add more integrations?{' '}
    <Link
      onClick={() => {
        navigateBookDemoClick();
      }}
    >
      upgrade your account
    </Link>
  </>
);

export const NoReposAfterOnboarding = () => {
  const Wrapper = styled(DarkBlueWrapper)`
    border-radius: 0.45rem;
    width: 100%;
    min-height: 4.5em;
  `;

  return (
    <Wrapper>
      <Flex items='center' justify='center' grow={1}>
        <Icon name='erroricon' color='torchRed' size='large' />
        <Flex items='baseline'>
          <Text pl='1rem' pr='1rem' size='large' weight='400' color='white'>
            We can't find any active repositories.
          </Text>
          <Text pr='1.5rem' color='white' size='medium'>
            Please add one or more repositories to your Git integrations
          </Text>
        </Flex>
      </Flex>
    </Wrapper>
  );
};

/**
 * Checks if the last backfill run for a repository occurred within the last 24 hours.
 * @param {object} repository
 * @returns {boolean}
 */
function isBackfillRunInLast24Hours(repository) {
  const lastBackfillStartedAt =
    repository?.extra?.last_backfill_started_at ?? null;

  return (
    lastBackfillStartedAt &&
    moment().utc().diff(moment(lastBackfillStartedAt), 'hours') <= 24
  );
}
